import { useCallback, useEffect, useMemo } from "react";
import { RecruiterActivity } from "redux/models/recruiterActivity";
import { useAPIRequest } from "hooks/useAPIRequest";
import { Modal } from "common/components/modal/modal";
import { Flex } from "common/components/flex/flex";
import { Skeleton } from "common/components/skeleton/skeleton";
import EmptyPlaceholder from "common/components/emptyPlaceholder/emptyPlaceholder";
import { displayDollar } from "utilities/tableUtility";
import { sumBy } from "lodash";
import { Table } from "common/components/table/table";
import { SectionError } from "../../../../components/sectionError";
import { TableColumns } from "./tableColumns";
import { EligibilityTag } from "../../../../components/eligibilityTag/eligibilityTag";
import styles from "./estimatedCommissionModal.module.scss";

const renderSummary = (totalBillings, totalAmount, totalCommission) => (
  <Table.Summary>
    <Table.Summary.Row>
      <Table.Summary.Cell index={0} colSpan={5}>
        <strong>Total ({totalBillings})</strong>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={5} align="center">
        <strong>{displayDollar(totalAmount)}</strong>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={6} align="center">
        <strong>{displayDollar(totalCommission)}</strong>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  </Table.Summary>
);

export const EstimatedCommissionModal = ({
  open,
  onClose,
  clientId,
  recruiterId,
  isEligible,
}) => {
  const title = (
    <Flex align="center" gap={8}>
      <span>Estimated Commission</span>
      <EligibilityTag isEligible={isEligible} />
    </Flex>
  );

  const fetchBillings = useCallback(
    () => RecruiterActivity.monthlyBillings(clientId, recruiterId, false),
    [clientId, recruiterId],
  );

  const { data, loading, error, execute } = useAPIRequest(fetchBillings, {
    initialData: [],
    initialLoading: true,
  });

  const totalAmount = useMemo(() => sumBy(data, "recruiterAmount"), [data]);
  const totalCommission = useMemo(
    () => sumBy(data, "recruiterCommission"),
    [data],
  );

  const renderContent = () => {
    if (loading) {
      return <Skeleton paragraph={{ rows: 13, width: "100%" }} />;
    }

    if (error) {
      return (
        <SectionError title="Failed to load data" message={error.message} />
      );
    }

    if (data.length === 0) {
      return (
        <Flex align="center" justify="center" style={{ flexGrow: 1 }}>
          <EmptyPlaceholder description="No billing data for this month" />
        </Flex>
      );
    }

    return (
      <Table
        columns={TableColumns}
        dataSource={data}
        pagination={{ pageSize: 6, showSizeChanger: false }}
        size="small"
        rowKey="id"
        summary={() => renderSummary(data.length, totalAmount, totalCommission)}
      />
    );
  };

  useEffect(() => {
    if (clientId && recruiterId) {
      execute();
    }
  }, [clientId, recruiterId, execute]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      footer={null}
      rowId="id"
      width={1000}
    >
      <div className={styles.tableWrapper}>{renderContent()}</div>
    </Modal>
  );
};
